import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

interface ResultsProps {
  className?: string;
}

const resultsData = [
  {
    task: "ART-E",
    openAIo3: 90,
    qwen25_14B: 41,
    manual_RL: 96,
    ruler_RL: 95,
  },
  {
    task: "Reasoning Classifier",
    openAIo3: 86,
    qwen25_14B: 60,
    manual_RL: 89,
    ruler_RL: 90,
  },
  {
    task: "Voice Ordering",
    openAIo3: 95,
    openAIo3Note: "†",
    qwen25_14B: 73,
    manual_RL: 95,
    ruler_RL: 96,
  },
  {
    task: "Customer Support Agent",
    openAIo3: 50,
    qwen25_14B: 62,
    manual_RL: 92,
    ruler_RL: 93,
  },
];

const Results: React.FC<ResultsProps> = ({ className = "" }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  const initChart = () => {
    if (!chartRef.current) return;

    // Dispose existing chart if any
    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
    }

    // Check if dark mode is active
    const isDarkMode = document.documentElement.classList.contains("dark");

    const chart = echarts.init(chartRef.current, isDarkMode ? "dark" : null);
    chartInstanceRef.current = chart;

    const textColor = isDarkMode ? "#e5e7eb" : "#1f2937";
    const axisLineColor = isDarkMode ? "#374151" : "#e5e7eb";

    const option: echarts.EChartsOption = {
      title: {
        text: "RULER Performance Comparison",
        left: "center",
        top: 20,
        textStyle: {
          fontSize: 18,
          fontWeight: "bold",
          color: textColor,
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          const taskName = params[0].axisValue;
          let result = `<strong>${taskName}</strong><br/>`;
          const uniqueSeries = new Map();
          
          params.forEach((param: any) => {
            // Skip duplicate "Qwen 2.5 Instruct" entries from stacked bars
            if (param.seriesName === "Qwen 2.5 Instruct" && (param.data === null || param.seriesIndex > 1)) {
              return;
            }
            
            // For stacked bars, show the total value
            let value = param.value;
            if (param.seriesName === "Qwen 2.5+Manual RL" || param.seriesName === "Qwen 2.5+RULER RL") {
              const taskIndex = param.dataIndex;
              if (param.seriesName === "Qwen 2.5+Manual RL") {
                value = resultsData[taskIndex].manual_RL;
              } else if (param.seriesName === "Qwen 2.5+RULER RL") {
                value = resultsData[taskIndex].ruler_RL;
              }
            }
            
            const displayValue = value === null ? "N/A" : `${value}%`;
            if (!uniqueSeries.has(param.seriesName)) {
              uniqueSeries.set(param.seriesName, `${param.marker} ${param.seriesName}: ${displayValue}`);
            }
          });
          
          uniqueSeries.forEach((line) => {
            result += line + '<br/>';
          });
          
          return result;
        },
      },
      legend: {
        data: [
          "OpenAI o3",
          "Qwen 2.5 Instruct",
          "Qwen 2.5+Manual RL",
          "Qwen 2.5+RULER RL",
        ],
        bottom: 5,
        left: "center",
        orient: "horizontal",
        itemGap: window.innerWidth < 768 ? 10 : 20,
        textStyle: {
          color: textColor,
          fontSize: window.innerWidth < 768 ? 10 : 12,
        },
        selectedMode: false,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: window.innerWidth < 768 ? "18%" : "15%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: resultsData.map((d) => d.task),
        axisLabel: {
          interval: 0,
          rotate: window.innerWidth < 768 ? 45 : 0,
          color: textColor,
          fontSize: window.innerWidth < 768 ? 10 : 12,
        },
        axisLine: {
          lineStyle: {
            color: axisLineColor,
          },
        },
      },
      yAxis: {
        type: "value",
        name: "Success Rate (%)",
        nameTextStyle: {
          color: textColor,
        },
        min: 0,
        max: 100,
        axisLabel: {
          formatter: "{value}%",
          color: textColor,
        },
        axisLine: {
          lineStyle: {
            color: axisLineColor,
          },
        },
        splitLine: {
          lineStyle: {
            color: axisLineColor,
          },
        },
      },
      series: [
        {
          name: "OpenAI o3",
          type: "bar",
          barGap: "20%",
          barCategoryGap: "40%",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.openAIo3),
          itemStyle: {
            color: "#3B82F6",
          },
        },
        {
          name: "Qwen 2.5 Instruct",
          type: "bar",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.qwen25_14B),
          itemStyle: {
            color: "#10B981",
          },
        },
        // Base layer for Manual RL (showing Qwen 2.5 baseline)
        {
          name: "Qwen 2.5 Instruct",
          type: "bar",
          stack: "manual",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.qwen25_14B),
          itemStyle: {
            color: "#10B981",
          },
          silent: true,
          barGap: "20%",
        },
        // Improvement layer for Manual RL
        {
          name: "Qwen 2.5+Manual RL",
          type: "bar",
          stack: "manual",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.manual_RL - d.qwen25_14B),
          itemStyle: {
            color: "#C084FC",
          },
        },
        // Base layer for RULER RL (showing Qwen 2.5 baseline)
        {
          name: "Qwen 2.5 Instruct",
          type: "bar",
          stack: "ruler",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.qwen25_14B),
          itemStyle: {
            color: "#10B981",
          },
          silent: true,
        },
        // Improvement layer for RULER RL
        {
          name: "Qwen 2.5+RULER RL",
          type: "bar",
          stack: "ruler",
          emphasis: {
            focus: "series",
          },
          data: resultsData.map((d) => d.ruler_RL - d.qwen25_14B),
          itemStyle: {
            color: "#F59E0B",
          },
          label: {
            show: true,
            position: "top",
            formatter: function (params: any) {
              const taskIndex = params.dataIndex;
              const totalValue = resultsData[taskIndex].ruler_RL;
              return totalValue + "%";
            },
            fontWeight: "bold",
          },
        },
      ],
    };

    chart.setOption(option);
  };

  useEffect(() => {
    initChart();

    const handleResize = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    // Observer for theme changes
    const observer = new MutationObserver(() => {
      initChart();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
      }
    };
  }, []);

  return (
    <div className={className}>
      <div className="results-chart mb-8">
        <div ref={chartRef} style={{ width: "100%", height: "500px" }}></div>
      </div>

      <div className="results-table-container">
        <div className="overflow-x-auto">
          <table className="results-table min-w-[600px]">
            <thead>
              <tr>
                <th>Task</th>
                <th>OpenAI o3</th>
                <th>Qwen 2.5*</th>
                <th>Qwen 2.5+Manual RL</th>
                <th>
                  <strong>Qwen 2.5+RULER RL</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {resultsData.map((row) => {
                const scores = [
                  { value: row.openAIo3, key: "openAIo3" },
                  { value: row.qwen25_14B, key: "qwen25_14B" },
                  { value: row.manual_RL, key: "manual_RL" },
                  { value: row.ruler_RL, key: "ruler_RL" },
                ];
                const highestScore = Math.max(...scores.map((s) => s.value));

                return (
                  <tr key={row.task}>
                    <td>{row.task}</td>
                    <td>
                      {row.openAIo3 === highestScore ? (
                        <strong>{row.openAIo3}%</strong>
                      ) : (
                        `${row.openAIo3}%`
                      )}
                      {(row as any).openAIo3Note || ""}
                    </td>
                    <td>
                      {row.qwen25_14B === highestScore ? (
                        <strong>{row.qwen25_14B}%</strong>
                      ) : (
                        `${row.qwen25_14B}%`
                      )}
                      {(row as any).qwen25_14BNote || ""}
                    </td>
                    <td>
                      {row.manual_RL === highestScore ? (
                        <strong>{row.manual_RL}%</strong>
                      ) : (
                        `${row.manual_RL}%`
                      )}
                    </td>
                    <td>
                      {row.ruler_RL === highestScore ? (
                        <strong>{row.ruler_RL}%</strong>
                      ) : (
                        `${row.ruler_RL}%`
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-sm mt-2">
          <em>
            * We used Qwen 2.5 14B Instruct as the base model for all tasks
            except the Reasoning Classifier, where we used the much smaller Qwen
            2.5 1.5B Instruct to meet customer requirements for edge deployment.
          </em>
          <div className="mt-3">
            <em>
              † We did not benchmark the Voice Ordering task with o3, since it
              is a realtime voice application that can't tolerate the latency of
              a reasoning model. We show GPT-4.1 results instead.
            </em>
          </div>
        </div>
        <div className="text-center mt-4 text-sm">
          <em>
            More information about each task is available in the{" "}
            <a href="#task-appendix">appendix</a>.
          </em>
        </div>
      </div>
    </div>
  );
};

export default Results;
